/**
 * @file     preApprovalFlow.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the three-answer affordability estimate, the broker referral sheet, and legacy readiness copy.
 */
export const preApprovalFlow = {
  seo: {
    title: 'Home Affordability Estimate for BC',
    description:
      'Three answers, no credit check: estimate the home price your income, debts and down payment could carry, worked out the way a BC lender tests it.'
  },
  page: {
    inputsLabel: 'Your three answers'
  },
  actions: {
    continue: 'Continue'
  },
  crossLink: {
    mortgageCalculator: 'Read next:',
    mortgageCalculatorAnchor: 'How to get pre-approved in BC',
    stressTest: 'Read next:',
    stressTestAnchor: 'What the stress test means for a pre-approval',
    buyerPrograms: 'Read next:',
    buyerProgramsAnchor: 'Getting pre-approved as a first-time buyer',
    buyerGuide: 'The financing step comes before the searching step. Here is',
    buyerGuideAnchor: 'The pre-approval step, explained'
  },
  picker: {
    eyebrow: 'Affordability estimate · 1 of 3',
    checklistLink: 'See the home price your budget could carry'
  },
  income: {
    title: 'What is your household income before tax?',
    description: 'For everyone on the mortgage.',
    label: 'Household income',
    under60k: 'Under $60k',
    '60to90k': '$60k to $90k',
    '90to120k': '$90k to $120k',
    '120to160k': '$120k to $160k',
    over160k: 'Over $160k',
    '160to200k': '$160k to $200k',
    '200to250k': '$200k to $250k',
    '250kPlus': 'Over $250k',
    preferNot: 'Rather not say'
  },
  debt: {
    title: 'How much do you pay toward debts each month?',
    description: 'Car loans, student loans, credit cards, lines of credit and support payments.',
    label: 'Monthly debts',
    none: 'None',
    under500: 'Under $500',
    '500to1000': '$500 to $1,000',
    '1000to2000': '$1,000 to $2,000',
    '2000Plus': 'Over $2,000'
  },
  downPaymentAmount: {
    title: 'How much do you have for a down payment?',
    description: 'Savings plus any confirmed family gift. Not closing costs.',
    label: 'Down payment saved',
    under25k: 'Under $25k',
    '25to50k': '$25k to $50k',
    '50to100k': '$50k to $100k',
    '100to200k': '$100k to $200k',
    '200kPlus': 'Over $200k'
  },
  incomeDetail: {
    title: 'Where in that range does it land?',
    description: 'Optional.',
    label: 'Narrow it down',
    unsure: 'Not sure',
    '0to35k': 'Under $35k',
    '35to45k': '$35k to $45k',
    '45to60k': '$45k to $60k',
    '60to70k': '$60k to $70k',
    '70to80k': '$70k to $80k',
    '80to90k': '$80k to $90k',
    '90to100k': '$90k to $100k',
    '100to110k': '$100k to $110k',
    '110to120k': '$110k to $120k',
    '120to135k': '$120k to $135k',
    '135to150k': '$135k to $150k',
    '150to160k': '$150k to $160k',
    '160to200k': '$160k to $200k',
    '200to250k': '$200k to $250k',
    '250kPlus': 'Over $250k'
  },
  situation: {
    title: 'Where are you in the process?',
    description: 'This sets what the rest of the questions are worth asking.',
    statusLabel: 'Pre-approval',
    notStarted: 'Not started',
    talkingToLender: 'Talking to a lender',
    havePreapproval: 'Already have one',
    expired: 'Had one, it expired',
    timelineLabel: 'Buying timeline'
  },
  timeline: {
    '0to3': 'Within 3 months',
    '3to6': '3 to 6 months',
    '6to12': '6 to 12 months',
    '12plus': 'More than a year',
    researching: 'Just researching'
  },
  downpayment: {
    title: 'How is the down payment coming together?',
    description: 'Lenders want to see ninety days of history on whatever account it sits in.',
    label: 'Down payment',
    savedFull: 'Saved and ready',
    savedPartial: 'Partly saved',
    giftExpected: 'Expecting a gift from family',
    notYet: 'Not started',
    coApplicantLabel: 'Applying'
  },
  coApplicant: {
    solo: 'On your own',
    withPartner: 'With a partner',
    withFamily: 'With family'
  },
  work: {
    title: 'How are you paid, and how is your credit?',
    description: 'How you are paid decides which documents a lender asks for.',
    employmentLabel: 'Employment',
    salaried: 'Salaried',
    hourly: 'Hourly',
    selfEmployed: 'Self-employed',
    commission: 'Commission',
    newJob: 'Started a new job recently',
    creditLabel: 'Credit'
  },
  credit: {
    strong: 'Strong',
    unsure: 'Not sure',
    workingOnIt: 'Working on it'
  },
  location: {
    title: 'Where are you looking?',
    description: 'The document list is the same across BC. The price is not.',
    label: 'City or area',
    placeholder: 'Start typing a city',
    budgetLabel: 'Price range in mind'
  },
  budget: {
    under600k: 'Under $600k',
    '600to800k': '$600k to $800k',
    '800kTo1m': '$800k to $1M',
    '1mTo15m': '$1M to $1.5M',
    over15m: 'Over $1.5M',
    notSure: 'Not sure yet'
  },
  readiness: {
    title: 'Where you stand',
    cta: 'Get ready for a pre-approval',
    gapsLabel: 'Worth settling',
    headlineReady: 'You have what a lender asks for',
    headlineNearly: 'Close. Two things to settle first',
    headlineEarly: 'Worth getting the groundwork in place first',
    bandReady:
      'On your answers, the pieces a lender starts with are in place. The next step is assembling the documents and booking the conversation.',
    bandNearly:
      'On your answers, most of what a lender starts with is in place. Two things are worth settling before the conversation.',
    bandEarly:
      'On your answers, this is the groundwork stage. That is a normal place to be, and it is easier to fix now than mid-offer.',
    gapDownNotYet:
      'The down payment is the first gate. A lender wants ninety days of history on the account holding it, so the clock on that history starts the day the money lands.',
    gapDownGift:
      'A gifted down payment needs a signed gift letter from the family member providing it, and the funds need to be in your account with the history behind them.',
    gapNewJob:
      'A recent job change is the most common reason a strong file stalls. Lenders usually want to see you past probation, and a letter of employment stating your start date.',
    gapSelfEmployed:
      'Self-employed and commission income is documented differently: two years of T1 Generals with the statement of business activities, two years of Notices of Assessment, and taxes paid up to date.',
    gapCreditWorking:
      'Credit is worth checking before a lender does. You can pull your own report without affecting your score, and errors take time to correct.',
    gapCreditUnsure:
      'Pull your own credit report before the lender pulls theirs. It does not affect your score, and it removes the one unknown you can remove.',
    gapExpired:
      'An expired pre-approval usually renews on current documents rather than starting over. Ask the lender what they still have on file.',
    gapSoonAndEarly:
      'A timeline inside three months and an early file is the combination worth talking to a mortgage broker about soonest.',
    noFurtherGaps: "Everything else on the lender's list is documentation rather than a decision.",
    closing:
      'This reflects only your answers. It is not a pre-approval, no credit was checked, and no lender has seen anything.'
  },
  broker: {
    title: 'Get introduced to mortgage brokers',
    description:
      'Learn how to get a pre-approval from independent mortgage brokers. Your answers and estimate go with the introduction.',
    submit: 'Get introduced',
    disclosure:
      'Referral fee: if you work with a broker Ray introduces and a mortgage closes, that broker may pay Ray Rasouli a referral fee. The broker pays it, it adds no cost to you, and you are under no obligation to use any broker.',
    disclosureLink: 'Referral disclosure',
    consent:
      'Ray will introduce you to one or more independent mortgage brokers. Each may contact you by email or phone about the pre-approval you asked about. Ray Rasouli does not arrange mortgages.',
    confirm:
      'Open the link sent to {{email}} so Ray can introduce you to mortgage brokers.',
    confirmTitle: 'One step left',
    sent: 'Ray is introducing you to one or more independent mortgage brokers. Each may contact you by email or phone.',
    sentTitle: 'Sent',
    after: 'You are under no obligation to use any broker. How the referral fee works is on the',
    afterTail: 'page.',
    done: 'Done'
  },
  brokerIntro: {
    lead: 'Learn how to get a pre-approval from independent mortgage brokers.',
    title: 'Get introduced to mortgage brokers',
    cta: 'Learn how to get pre-approved',
    sent: 'Ray is introducing you to one or more independent mortgage brokers. Each may contact you by email or phone.',
    share: 'Share my details and income with the independent mortgage brokers Ray introduces.'
  },
  documents: {
    employedLabel: 'Employed',
    newJobLabel: 'Recently started a new job',
    everyoneId: 'Government photo identification.',
    everyoneHistory: 'Ninety days of history on the account holding your down payment.',
    everyoneDeposits: 'A written explanation and paper trail for any large deposit in that window.',
    everyoneDebts: 'A list of your debts: balances, minimum payments, and limits.',
    everyoneSin: 'Your Social Insurance Number, for the credit check.',
    incomeProof: 'Income documents: recent pay stubs and an employment letter, or two years of tax returns and Notices of Assessment if self-employed.',
    employedLetter: 'A recent letter of employment stating position, salary, and start date.',
    employedStubs: 'Your two most recent pay stubs.',
    employedT4s: 'Your two most recent T4 slips and Notices of Assessment.',
    selfT1s: 'Two years of T1 Generals with the full statement of business activities.',
    selfNoas: 'Two years of Notices of Assessment.',
    selfRegistration: 'Business registration or articles of incorporation.',
    selfTaxes: 'Confirmation that personal and business taxes are paid up to date.',
    giftLetter: 'A signed gift letter from the immediate family member providing it, stating the money is a gift and not a loan.',
    giftLanded: 'Proof the funds have landed in your account.'
  },
  result: {
    title: 'Your affordability estimate',
    eyebrow: 'Estimate',
    estimateLabel: 'Home price your answers could carry',
    value: 'About {{price}}',
    empty: 'Pick one answer in each group to see your estimate.',
    meaning:
      'With {{down}} down, a mortgage of about {{mortgage}}. At {{rate}}, about {{payment}} a month. Lenders test it at {{qualifying}}.',
    seeHomes: 'See homes at this price',
    sendFile: 'Get introduced to mortgage brokers',
    brokerStatus: 'Your broker request',
    disclaimer: 'An estimate, not a mortgage approval. Rates and lender rules vary.',
    assumptions:
      'Based on a {{rate}} rate, tested at {{qualifying}}, over {{years}} years. Includes property tax, heat, half a typical strata fee, and mortgage insurance under 20% down.',
    peekLabel: 'Estimate',
    peekAction: 'See homes'
  }
} as const;
